import { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';

import { ShortUrl } from '@air/api';
import { type InputFile, type OnUploadParams, UploadDropzone } from '@air/component-upload-dropzone';
import {
  MAX_ANCESTOR_COUNT_EXCEEDED,
  matchesAirror,
  NO_MORE_CREDITS,
  NO_MORE_STORAGE,
  SHARE_OBJECT_NOT_FOUND,
} from '@air/errors';
import { contentCollectionFormSubmissionRequiredSelector } from '@air/feature-content-collection/redux';
import { useAirModal } from '@air/provider-modal';
import { useShortIdContext } from '@air/provider-short-id';
import { useToasts } from '@air/provider-toast';
import { queueUploadsAction, rejectUploadsAction, uploadArraySelector } from '@air/redux-uploader';
import { createUpload } from '@air/upload-utils';
import { reportErrorToBugsnag } from '@air/utils-error';

import { ContentCollectionMaxItemCountModal } from '~/components/ContentCollection/ContentCollectionMaxItemCountModal';
import { MAX_FORM_SESSION_ITEMS } from '~/components/ContentCollection/constants';
import { useContentCollectionUploadsManager } from '~/components/ContentCollection/hooks/useContentCollectionUploadsManager';
import { useGetContentCollectionSessionId } from '~/components/ContentCollection/hooks/useGetContentCollectionSessionId';
import { MaxFolderDepthModal } from '~/components/ContentCollection/MaxFolderDepthModal';
import { OutOfStorageModal } from '~/components/ContentCollection/OutOfStorageModal';
import {
  calculateItemCountFromContentCollectionUploads,
  getSortedFilesAndMetadata,
} from '~/components/ContentCollection/utils';
import { MissingFileExtensionsModal } from '~/components/Modals/MissingFileExtenionsModal';
import { RestrictedCharacterReplacementModal } from '~/components/Modals/RestrictedCharacterReplacementModal';
import { setPublicUploadMetadata } from '~/store/publicUploadMetadata/actions';
import { getFilePath } from '~/store/uploadingPathToBoardId/utils';
import { sanitizeRestrictedPathChars, sanitizeRestrictedPathCharsInFile } from '~/utils/FileUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export type ContentCollectionDropzoneProps = {
  children: JSX.Element;
};

const createUploadWithBoardRelativePath = ({ file, batchId }: { file: InputFile; batchId: string }) => {
  const upload = createUpload({ file, batchId });
  upload.apiUploadData = {
    ...upload.apiUploadData,
    boardRelativePath: sanitizeRestrictedPathChars(file.path ? getFilePath(file.path) : ''),
    uploadGroupId: batchId,
  };
  return upload;
};

export const ContentCollectionDropzone = memo(({ children }: ContentCollectionDropzoneProps) => {
  const { shortId } = useShortIdContext();
  const { getSessionId } = useGetContentCollectionSessionId();
  const formSubmissionRequired = useSelector(contentCollectionFormSubmissionRequiredSelector);

  const [showOutOfStorageModal] = useAirModal(OutOfStorageModal);
  const [showMaxFolderDepthModal] = useAirModal(MaxFolderDepthModal);
  const [showMissingFileExtensionsModal] = useAirModal(MissingFileExtensionsModal);
  const [showRestrictedCharacterConfirmationModal] = useAirModal(RestrictedCharacterReplacementModal);
  const [showMaxItemCountModal] = useAirModal(ContentCollectionMaxItemCountModal);
  const { showToast } = useToasts();
  const store = useAirStore();
  const dispatch = useDispatch();

  useContentCollectionUploadsManager();

  const startUpload = async ({ acceptedFiles, rejectedFiles }: OnUploadParams) => {
    const { sessionId } = await getSessionId();

    const { emptyFiles, filesWithNoExtension, filesWithRestrictedChars, files, totalSize, depthIncrease } =
      getSortedFilesAndMetadata(acceptedFiles);

    let canUpload = false;
    let failureReason: string | undefined;

    try {
      const response = await ShortUrl.checkCanUpload(shortId, totalSize, depthIncrease);

      canUpload = response.canUpload;
      failureReason = response.failureReason;
    } catch (error) {
      if (matchesAirror(error, SHARE_OBJECT_NOT_FOUND)) {
        showToast('The share link you’re trying to upload to is no longer available', { color: 'red' });
      } else {
        showToast('Something went wrong. Please try again later.', { color: 'red' });
      }

      return;
    }

    if (!canUpload) {
      switch (failureReason) {
        /**
         * Note we're using the same modal when cannot upload due to both plan storage and
         * credit depletion scenarios, because the public-facing modal is generic enough to
         * handle both cases for the time being.
         */
        case NO_MORE_STORAGE.message:
        case NO_MORE_CREDITS.message:
          return showOutOfStorageModal();

        case MAX_ANCESTOR_COUNT_EXCEEDED.message:
          return showMaxFolderDepthModal();

        default: {
          reportErrorToBugsnag({
            error: new Error(`Uploading to a public dropzone failed`),
            context: `Uploading to a public dropzone failed`,
            metadata: {
              data: {
                failureReason,
                shortId,
              },
            },
          });

          return showToast('Something went wrong. Please try again later.');
        }
      }
    }

    // If there are any files without extensions, prompt the user to cancel or continue without those files.
    if (filesWithNoExtension.length) {
      const shouldCancelUpload = await new Promise((resolve) => {
        showMissingFileExtensionsModal({
          items: filesWithNoExtension,
          onCancel: () => {
            resolve(true);
          },
          onConfirm: () => {
            resolve(false);
          },
        });
      });

      if (shouldCancelUpload) {
        return;
      }
    }

    // If there are any file with names that contain restricted characters, prompt the user to confirm the replacement of these characters.
    if (filesWithRestrictedChars.length) {
      const { confirmed } = await new Promise<{ confirmed: boolean }>((resolve) => {
        showRestrictedCharacterConfirmationModal({
          items: filesWithRestrictedChars,
          onCancel: () => {
            resolve({ confirmed: false });
          },
          onConfirm: () => {
            resolve({ confirmed: true });
          },
        });
      });

      if (!confirmed) {
        return;
      } else {
        const sanitizedFiles = filesWithRestrictedChars.map((file) => sanitizeRestrictedPathCharsInFile(file));
        files.push(...sanitizedFiles);
      }
    }

    if (files.length) {
      const batchId = v4();
      const uploads = files.map((file) => createUploadWithBoardRelativePath({ file, batchId }));

      const itemCount = calculateItemCountFromContentCollectionUploads(uploadArraySelector(store.getState()));
      const newItemCount = calculateItemCountFromContentCollectionUploads(uploads);

      if (itemCount + newItemCount > MAX_FORM_SESSION_ITEMS) {
        return showMaxItemCountModal();
      }

      dispatch(queueUploadsAction({ uploads }));
      uploads.forEach((upload) => {
        dispatch(
          setPublicUploadMetadata({
            uploadId: upload.id,
            metadata: {
              sessionId,
              shortBoardId: shortId,
            },
          }),
        );
      });
    }

    if (rejectedFiles.length + emptyFiles.length + filesWithNoExtension.length > 0) {
      const mapWithReason = (reason: 'unsupported' | 'empty' | 'noExtension') => (files: InputFile[]) =>
        files.map(({ name }) => ({ name, reason }));

      dispatch(
        rejectUploadsAction({
          files: [
            ...mapWithReason('unsupported')(rejectedFiles),
            ...mapWithReason('empty')(emptyFiles),
            ...mapWithReason('noExtension')(filesWithNoExtension),
          ],
        }),
      );
    }
  };

  return (
    <UploadDropzone onUpload={startUpload} disabled={formSubmissionRequired}>
      {children}
    </UploadDropzone>
  );
});

ContentCollectionDropzone.displayName = 'ContentCollectionDropzone';
